import { useCallback, useEffect, useState } from "react";

export interface AsyncResult<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | undefined;
  /** Re-run the async function. */
  reload: () => void;
  /** Locally update the loaded data (e.g. after a mutation) without refetching. */
  setData: (updater: (prev: T | undefined) => T | undefined) => void;
}

/**
 * Run an async function on mount and whenever `deps` change, tracking loading
 * and error state. Results from stale runs are ignored.
 *
 * `fn` is intentionally excluded from the effect dependencies — callers control
 * re-runs via the `deps` array (which should include everything `fn` reads).
 */
export function useAsync<T>(fn: () => Promise<T>, deps: readonly unknown[]): AsyncResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [nonce, setNonce] = useState<number>(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    fn().then(
      (result) => {
        if (!active) return;
        setData(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const update = useCallback((updater: (prev: T | undefined) => T | undefined) => {
    setData((prev) => updater(prev));
  }, []);

  return { data, loading, error, reload, setData: update };
}
